import React from 'react';
import { StockLocation } from '../../../types/stock_location/stockLocation';
import OrderStatusLabel from './OrderStatusLabel';
import { Order, OrderUtil } from '../../../types/order/';
import { SystemUser, User } from '../../../types/user/user';
import { TableHeader } from '../../components/Table';
import { toMap } from '../../../util/map.util';
import { toFilterString } from '../../../util/string.util';
import { CustomFieldUtil } from '../../../types/custom_field/customField.util';
import { Company } from '../../../types/company';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { t, translate } from '../../../types/translation/Translator';
import { Contact } from '../../../types/contact';
import { PdfTemplateEntityType } from '../../../types/pdf_template';
import GeneratePdfButton from '../../components/Button/templates/GeneratePdfButton';
import { TableSettings } from '../../../types/table_settings/TableSettings';
import { OrderTableSettingsColumn } from '../../../types/table_settings/order/OrderTableSettings';
import dayjs from 'dayjs';
import { CustomFieldType } from '../../../types/custom_field';

export const orderTableHeaders = (
  stockLocations: Map<string, StockLocation>,
  users: Map<string, User>,
  contacts: Map<string, Contact>,
  settings: TableSettings<any>,
  companies: Map<string, Company>,
  company: Company,
  locale: string,
) => {
  const wantedColumns = toMap(settings.columns, 'column');

  const columns: TableHeader<Order>[] = [];

  const isPartner = company.partner;

  if (isPartner) {
    columns.push({
      key: 'company',
      name: t(TranslationKey.company).singular.label,
      text: (item: Order) => companies.get(item.companyId)?.name || '',
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.order)) {
    columns.push({
      key: 'number',
      name: wantedColumns.get(OrderTableSettingsColumn.order)?.label || '',
      text: (item: Order) => item.number || 'Unknown Order Number',
      column: OrderTableSettingsColumn.order,
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.type)) {
    columns.push({
      key: 'type',
      name: wantedColumns.get(OrderTableSettingsColumn.type)?.label || '',
      text: (item: Order) => OrderUtil.typeToString(item.type),
      column: OrderTableSettingsColumn.type,
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.createdAt)) {
    columns.push({
      key: 'createdAt',
      name: wantedColumns.get(OrderTableSettingsColumn.createdAt)?.label || '',
      text: (item: Order) => `${new Date(item.createdAt).toLocaleDateString(locale)}`,
      column: OrderTableSettingsColumn.createdAt,
      sortValue: (item: Order) => dayjs(item.createdAt),
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.contact)) {
    columns.push({
      key: 'contactName',
      name: wantedColumns.get(OrderTableSettingsColumn.contact)?.label || '',
      text: (item: Order) => contacts.get(item.contactId || '')?.name || '',
      column: OrderTableSettingsColumn.contact,
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.updatedAt)) {
    columns.push({
      key: 'updatedAt',
      name: wantedColumns.get(OrderTableSettingsColumn.updatedAt)?.label || '',
      text: (item: Order) => `${new Date(item.updatedAt).toLocaleDateString(locale)}`,
      column: OrderTableSettingsColumn.updatedAt,
      sortValue: (item: Order) => dayjs(item.updatedAt),
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.sourceStockLocation)) {
    columns.push({
      key: 'source',
      name: wantedColumns.get(OrderTableSettingsColumn.sourceStockLocation)?.label || '',
      text: (item: Order) =>
        item.fromStockLocationId ? stockLocations.get(item.fromStockLocationId)?.name || 'Unknown Stock Location' : '',
      column: OrderTableSettingsColumn.sourceStockLocation,
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.destinationStockLocation)) {
    columns.push({
      key: 'destination',
      name: wantedColumns.get(OrderTableSettingsColumn.destinationStockLocation)?.label || '',
      text: (item: Order) =>
        item.toStockLocationId ? stockLocations.get(item.toStockLocationId)?.name || 'Unknown Stock Location' : '',
      column: OrderTableSettingsColumn.destinationStockLocation,
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.status)) {
    columns.push({
      key: 'status',
      name: wantedColumns.get(OrderTableSettingsColumn.status)?.label || '',
      text: (item: Order) => <OrderStatusLabel status={item.status} />,
      column: OrderTableSettingsColumn.status,
      sortValue: (item: Order) => item.status,
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.createdBy)) {
    columns.push({
      key: 'createdBy',
      name: wantedColumns.get(OrderTableSettingsColumn.createdBy)?.label || '',
      text: (item: Order) => {
        return (
          users.get(item.createdBy)?.email || (item.createdBy === SystemUser.id ? SystemUser.email : 'Unknown user')
        );
      },
      column: OrderTableSettingsColumn.createdBy,
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.pdf)) {
    columns.push({
      key: 'pdf',
      name: wantedColumns.get(OrderTableSettingsColumn.pdf)?.label || '',
      text: (item: Order) => (
        <GeneratePdfButton
          type={'icon'}
          entityId={item.id}
          entityType={PdfTemplateEntityType.order}
          entitySubtype={OrderUtil.orderTypeToPdfTemplateEntitySubtype(item.type)}
        />
      ),
      column: OrderTableSettingsColumn.pdf,
    });
  }

  if (wantedColumns.has(OrderTableSettingsColumn.assignedTo)) {
    columns.push({
      key: 'assignedTo',
      name: wantedColumns.get(OrderTableSettingsColumn.assignedTo)?.label || '',
      text: (item: Order) => {
        const assignedTo = item.assignedTo;
        if (!assignedTo.length) return '';
        return assignedTo.map(id => users.get(id)?.email || translate({ key: TranslationKey.unknownUser })).join(', ');
      },
      column: OrderTableSettingsColumn.assignedTo,
    });
  }

  const defaultKeys = new Set(Object.keys(OrderTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

  customFieldColumns.forEach(item => {
    columns.push({
      key: item.column,
      name: item.label,
      sortValue: (order: Order) => {
        const customFieldValue = order.customFieldValues().find(i => i.name === item.column);
        if (!customFieldValue) return '';
        if (customFieldValue.type === CustomFieldType.date) return dayjs(customFieldValue.value);
        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
      text: (o: Order) => {
        const customFieldValue = o.customFieldValues().find(i => i.name === item.column);
        if (!customFieldValue) return '';
        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
      column: item.column,
    });
  });

  return columns.sort(
    (a: TableHeader<Order>, b: TableHeader<Order>) =>
      (wantedColumns.get(a.column || '')?.index || -1) - (wantedColumns.get(b.column || '')?.index || -1),
  );
};

export const orderTableFilter = (
  item: Order,
  filter: string,
  stockLocations: Map<string, StockLocation>,
  users: Map<string, User>,
  settings: TableSettings<any>,
) => {
  const filterString = toFilterString(filter);
  const wantedColumns = toMap(settings.columns, 'column');

  if (!filter) return true;

  if (wantedColumns.has(OrderTableSettingsColumn.order) && toFilterString(item.number).includes(filterString)) {
    return true;
  }

  if (wantedColumns.has(OrderTableSettingsColumn.status) && toFilterString(item.status).includes(filterString)) {
    return true;
  }

  if (wantedColumns.has(OrderTableSettingsColumn.type) && toFilterString(item.type).includes(filterString)) {
    return true;
  }

  if (
    wantedColumns.has(OrderTableSettingsColumn.createdAt) &&
    new Date(item.createdAt).toLocaleDateString().toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderTableSettingsColumn.updatedAt) &&
    new Date(item.updatedAt).toLocaleDateString().toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderTableSettingsColumn.destinationStockLocation) &&
    OrderUtil.hasToStockLocation(item) &&
    toFilterString(stockLocations.get(item.toStockLocationId || 'NULL')?.name).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderTableSettingsColumn.sourceStockLocation) &&
    OrderUtil.hasFromStockLocation(item) &&
    toFilterString(stockLocations.get(item.fromStockLocationId || 'NULL')?.name).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderTableSettingsColumn.createdBy) &&
    toFilterString(users.get(item.createdBy)?.email).includes(filterString)
  ) {
    return true;
  }

  const defaultKeys = new Set(Object.keys(OrderTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

  for (const custom of customFieldColumns) {
    const field = item
      .customFieldValues()
      .find(i => i.name === custom.column)
      ?.value.toLocaleLowerCase();
    if (field?.includes(filterString)) return true;
  }

  return false;
};
